// errorLogger.js - Клиентская система логирования ошибок

class ErrorLogger {
    constructor() {
        this.apiUrl = '/api/errors/log';
        this.queue = [];
        this.isSending = false;
        this.maxRetries = 3;
        this.retryDelay = 1000;
        
        this.init();
    }
    
    // Получаем данные пользователя динамически, чтобы избежать проблем с порядком загрузки модулей
    getCurrentUser() {
        try {
            // Пытаемся получить данные из localStorage напрямую
            const stored = localStorage.getItem('auth_user');
            if (stored) {
                const user = JSON.parse(stored);
                return {
                    user_id: user.id || null,
                    username: user.name || null
                };
            }
        } catch (error) {
            // Игнорируем ошибки при получении данных пользователя
        }
        return { user_id: null, username: null };
    }
    
    init() {
        // Сохраняем предыдущий обработчик, если он был
        const previousOnError = window.onerror;
        
        // Перехватываем глобальные ошибки JavaScript
        window.onerror = (message, source, lineno, colno, error) => {
            // Вызываем предыдущий обработчик, если он был
            if (previousOnError) {
                previousOnError(message, source, lineno, colno, error);
            }
            
            this.logError({
                error_type: 'error',
                message: message || 'Unknown error',
                stack: error?.stack || null,
                url: source || window.location.href,
                line: lineno,
                column: colno,
                additional_info: {
                    type: 'window.onerror'
                }
            });
            return false; // Не подавляем ошибку в консоли
        };
        
        // Перехватываем необработанные Promise rejection
        window.addEventListener('unhandledrejection', (event) => {
            const error = event.reason;
            
            // Логируем в консоль (браузер уже выведет, но мы добавим дополнительную информацию)
            console.error('Unhandled Promise Rejection:', error);
            
            this.logError({
                error_type: 'unhandledrejection',
                message: error?.message || String(error) || 'Unhandled Promise rejection',
                stack: error?.stack || null,
                url: window.location.href,
                additional_info: {
                    type: 'unhandledrejection',
                    reason: String(error)
                }
            });
        });
        
        // Перехватываем ошибки загрузки ресурсов
        window.addEventListener('error', (event) => {
            if (event.target !== window) {
                // Это ошибка загрузки ресурса (img, script, css)
                const target = event.target;
                
                // Логируем в консоль
                console.error(`Failed to load resource: ${target.src || target.href || 'unknown'}`, {
                    tagName: target.tagName,
                    src: target.src || target.href
                });
                
                this.logError({
                    error_type: 'resource',
                    message: `Failed to load resource: ${target.src || target.href || 'unknown'}`,
                    url: window.location.href,
                    additional_info: {
                        type: 'resource_error',
                        tagName: target.tagName,
                        src: target.src || target.href
                    }
                });
            }
        }, true);
        
        console.log('ErrorLogger initialized - все ошибки будут логироваться в консоль и отправляться на сервер');
    }
    
    // Проверяем, является ли user agent поисковым ботом
    isBot(userAgent) {
        if (!userAgent) return false;
        const ua = userAgent.toLowerCase();
        const botPatterns = [
            'bot', 'crawler', 'spider', 'scraper', 'crawl',
            'googlebot', 'bingbot', 'yandex', 'baiduspider',
            'slurp', 'duckduckbot', 'facebookexternalhit',
            'ia_archiver', 'msnbot', 'twitterbot', 'linkedinbot',
            'applebot', 'petalbot', 'ahrefsbot', 'semrushbot',
            'mj12bot', 'dotbot', 'megaindex', 'blexbot'
        ];
        return botPatterns.some(pattern => ua.includes(pattern));
    }
    
    // Проверяем, вызвана ли ошибка расширениями браузера/антивирусом
    isExtensionError(errorData) {
        if (!errorData) return false;
        
        const stack = (errorData.stack || '').toLowerCase();
        const message = (errorData.message || '').toLowerCase();
        
        // Проверяем признаки ошибок от расширений/антивируса
        const extensionPatterns = [
            'kaspersky-labs.com',
            'kaspersky',
            'avg',
            'avast',
            'norton',
            'mcafee',
            'bitdefender',
            'eset',
            'adblock',
            'ublock',
            'ghostery',
            'privacy',
            'extension',
            'chrome-extension://',
            'moz-extension://',
            'safari-extension://'
        ];
        
        // Если в stack есть признаки расширений
        if (stack && extensionPatterns.some(pattern => stack.includes(pattern))) {
            return true;
        }
        
        // Если ошибка "Failed to fetch" и в stack есть URL расширения
        if (message.includes('failed to fetch') && stack) {
            if (extensionPatterns.some(pattern => stack.includes(pattern))) {
                return true;
            }
        }
        
        return false;
    }
    
    // Добавляем ошибку в очередь и отправляем
    logError(errorData) {
        // Добавляем метаданные
        errorData.timestamp = new Date().toISOString();
        errorData.userAgent = navigator.userAgent;
        errorData.screenSize = `${window.innerWidth}x${window.innerHeight}`;
        errorData.url = errorData.url || window.location.href;
        
        // Проверяем, является ли запрос от поискового бота
        if (this.isBot(errorData.userAgent)) {
            // Логируем в консоль, но не отправляем на сервер
            this.logToConsole(errorData);
            console.log('Ошибка от бота - не отправляется на сервер');
            return;
        }
        
        // Проверяем, вызвана ли ошибка расширениями браузера/антивирусом
        if (this.isExtensionError(errorData)) {
            // Логируем в консоль, но не отправляем на сервер
            this.logToConsole(errorData);
            console.log('Ошибка вызвана расширением/антивирусом - не отправляется на сервер');
            return;
        }
        
        // Всегда добавляем user_id, если пользователь залогинен
        const user = this.getCurrentUser();
        if (user.user_id) {
            errorData.user_id = user.user_id;
            errorData.username = user.username;
        }
        
        // ВСЕГДА логируем ошибку в консоль перед отправкой на сервер
        this.logToConsole(errorData);
        
        this.queue.push(errorData);
        this.processQueue();
    }
    
    // Логирование ошибки в консоль
    logToConsole(errorData) {
        const errorType = errorData.error_type || 'error';
        const errorMessage = `[${errorType.toUpperCase()}] ${errorData.message || 'Unknown error'}`;
        const errorDetails = {
            type: errorType,
            message: errorData.message,
            url: errorData.url,
            line: errorData.line,
            column: errorData.column,
            stack: errorData.stack,
            additional_info: errorData.additional_info,
            timestamp: errorData.timestamp
        };
        
        // Выбираем уровень логирования в зависимости от типа ошибки
        switch (errorType) {
            case 'warning':
                console.warn(errorMessage, errorDetails);
                break;
            case 'resource':
                console.error(errorMessage, errorDetails);
                break;
            case 'unhandledrejection':
                console.error(errorMessage, errorDetails);
                if (errorData.stack) {
                    console.error('Stack trace:', errorData.stack);
                }
                break;
            case 'api_error':
                console.error(errorMessage, errorDetails);
                break;
            case 'custom':
                console.error(errorMessage, errorDetails);
                if (errorData.stack) {
                    console.error('Stack trace:', errorData.stack);
                }
                break;
            case 'error':
            default:
                console.error(errorMessage, errorDetails);
                if (errorData.stack) {
                    console.error('Stack trace:', errorData.stack);
                }
                break;
        }
    }
    
    // Метод для логирования предупреждений
    logWarning(message, additionalInfo = {}) {
        this.logError({
            error_type: 'warning',
            message: message,
            additional_info: additionalInfo
        });
    }
    
    // Метод для логирования кастомных ошибок
    logCustomError(message, stack = null, additionalInfo = {}) {
        this.logError({
            error_type: 'custom',
            message: message,
            stack: stack,
            additional_info: additionalInfo
        });
    }
    
    // Обработка очереди отправки
    async processQueue() {
        if (this.isSending || this.queue.length === 0) {
            return;
        }
        
        this.isSending = true;
        
        while (this.queue.length > 0) {
            const errorData = this.queue.shift();
            await this.sendError(errorData);
        }
        
        this.isSending = false;
    }
    
    // Отправка ошибки на сервер
    async sendError(errorData, retryCount = 0) {
        try {
            const response = await fetch(this.apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(errorData)
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
        } catch (error) {
            // Повторяем попытку при неудаче
            if (retryCount < this.maxRetries) {
                await new Promise(resolve => setTimeout(resolve, this.retryDelay * (retryCount + 1)));
                return this.sendError(errorData, retryCount + 1);
            }
            // Если все попытки неудачны, логируем в консоль
            console.error('Failed to send error to server:', errorData, error);
        }
    }
    
    // Обёртка для try-catch с автоматическим логированием
    wrapAsync(fn, context = 'async operation') {
        return async (...args) => {
            try {
                return await fn(...args);
            } catch (error) {
                this.logError({
                    error_type: 'error',
                    message: error.message,
                    stack: error.stack,
                    additional_info: {
                        context: context,
                        args: args.map(a => typeof a === 'object' ? '[Object]' : String(a))
                    }
                });
                throw error;
            }
        };
    }
}

// Создаём глобальный экземпляр
export const errorLogger = new ErrorLogger();

// Экспортируем также класс для возможного расширения
export default ErrorLogger;

